<template>
  <div class="submenu px-4 d-flex justify-center mx-auto bg-bgDefault">
    <div class="submenu-container h-100 w-100 d-flex">
      <v-col class="d-flex pa-0 h-100 flex-grow-0">
        <DocumentAtsDropdown
          class="mr-2 mt-auto mb-auto"
        />
        <SyncModalButton
          v-if="showSyncOrExport && isSyncable"
          :job-id="jobId"
          :ats-edit-url="atsEditUrl"
          :ats-name="atsName"
          :ats-icon="atsIcon"
          :location="location"
          :company="company"
          :title="jobTitle"
          @tracking-event="$emit('tracking-event', $event)"
        />
      </v-col>

      <v-spacer />

      <v-col class="d-flex pa-0 h-100 flex-grow-1 ga-4">
        <v-btn
          v-if="!isSharePage"
          class="text-button-2 my-auto pa-0"
          variant="plain"
          @click="duplicateModalVisible = true"
        >
          Duplicate
        </v-btn>

        <v-divider
          v-if="(isEditable && documentType === 'job' && !hideApplyTemplate)
            || translateJobVisible
            || (!isTemplate && !authData.nextUiEnabled)"
          class="mt-3 mb-3"
          vertical
        />

        <!-- crawled jobs and templates have incompatible fields, so hide for published/unpublished jobs -->
        <v-btn
          v-if="isEditable && documentType === 'job' && !hideApplyTemplate"
          class="text-button-2 my-auto pa-0"
          variant="plain"
          @click="setApplyTemplateModalVisible(true)"
        >
          Apply Template
        </v-btn>

        <TranslateJobButton />

        <!-- TODO: remove after new similar jobs implementation is complete -->
        <v-btn
          v-if="!isTemplate && !authData.nextUiEnabled"
          class="text-button-2 my-auto pa-0"
          variant="plain"
        >
          <SimilarJobs
            :job-id="jobId"
            @tracking-event="$emit('tracking-event', $event)"
          />
        </v-btn>

        <v-divider
          v-if="!isTemplate"
          class="mt-3 mb-3"
          vertical
        />

        <v-btn
          class="text-button-2 my-auto pa-0"
          min-width="0"
          variant="plain"
        >
          <ExportModalPostEditor
            :job-id="jobId"
            :title="jobTitle"
            :is-data-loaded="isDataLoaded"
            @tracking-event="$emit('tracking-event', $event)"
          />
        </v-btn>

        <FirstDraftButtonForSubmenu
          v-if="aiEnabled && !isTemplate"
          class="my-auto pa-0"
        />
      </v-col>

      <v-spacer />

      <v-col class="d-flex pa-0 h-100 flex-grow-0">
        <SimilarJobsToggle
          v-if="!isTemplate && authData.nextUiEnabled"
          class="pr-2 my-auto"
        />
        <HistoryToggle
          class="pr-2 my-auto"
        />
        <CommentsToggle
          class="pr-2 my-auto"
        />
        <NotifyDropdown
          v-if="!isSharePage
            && !isTemplate
            && projectIntegrationTypeKey !== TYPE_TAPWAGE
            && !isFlagship"
          :job-id="jobId"
          :owners-active="ownersActive"
          :owners-lead="ownersLead"
          :valid-users="validUsers"
          :is-linked="isLinked"
          :has-share-users="hasShareUsers"
          :show-invite="showInvite"
          :company-name="companyName"
          :is-data-loaded="isDataLoaded"
          @tracking-event="$emit('tracking-event', $event)"
        />
        <ShareModalButton
          v-if="!isSharePage && !isFlagship && projectIntegrationTypeKey === TYPE_TAPWAGE"
          :job-id="jobId"
          :valid-users="validUsers"
          :is-linked="isLinked"
          :has-share-users="hasShareUsers"
          :show-invite="showInvite"
          :company-name="companyName"
          :disabled="isTemplate && !isHidden"
        />
        <DocumentDropdown
          :job-id="jobId"
          :title="jobTitle"
          :tapwage-id="tapwageId"
          :superuser="superuser"
          :project-integration-type="projectIntegrationType"
          :project-integration-type-key="projectIntegrationTypeKey"
          :ats-edit-url="atsEditUrl"
          :has-integration="hasIntegration"
          :is-share-page="isSharePage"
          :is-template="isTemplate"
          :is-linked="isLinked"
          :is-open="isOpen"
          :is-published="isPublished"
          :is-private="isPrivate"
          :is-data-loaded="isDataLoaded"
          :is-syncable="isSyncable"
          @tracking-event="$emit('tracking-event', $event)"
          @duplicate="duplicateModalVisible = true"
        />
      </v-col>
    </div>

    <DuplicateModal
      :show="duplicateModalVisible"
      :document-type="documentType"
      :doc-title="jobTitle"
      @close="duplicateModalVisible = false"
      @duplicate="onDuplicate"
    />
  </div>
</template>
<script>
/* eslint-disable vue/require-prop-types */
import { mapActions, mapState } from 'pinia';
import { useApplyTemplate } from '@/stores/document/applyTemplate';
import { useAppAuthData } from '@/stores/app/authData';
import { useEditor } from '@/stores/editor/editor';
import { useLibrary } from '@/stores/library/library';
import { TYPE_TAPWAGE } from '@/helpers/constants/integrationTypes';
import SyncModalButton from '@/components/editor/SyncModalButton.vue';
import DocumentDropdown from '@/components/editor/DocumentDropdown.vue';
import ExportModalPostEditor from '@/components/editor/ExportModalPostEditor.vue';
// TODO: remove after new similar jobs implementation is complete
import SimilarJobs from '@/components/editor/SimilarJobs.vue';
import NotifyDropdown from '@/components/editor/NotifyDropdown.vue';
import ShareModalButton from '@/components/editor/ShareModalButton.vue';
import DuplicateModal from '@/components/shared/DuplicateModal.vue';
import DocumentAtsDropdown from '@/components/editor/DocumentAtsDropdown.vue';
import CommentsToggle from '@/components/document/rightPanel/CommentsToggle.vue';
import HistoryToggle from '@/components/document/rightPanel/HistoryToggle.vue';
import SimilarJobsToggle from '@/components/document/rightPanel/SimilarJobsToggle.vue';
import FirstDraftButtonForSubmenu from '@/components/firstDraft/FirstDraftButtonForSubmenu.vue';
import TranslateJobButton from '@/components/editor/TranslateJobButton.vue';
import { useTranslateJobButton } from '@/composables/useTranslateJobButton';

export default {
  name: 'SubMenu',
  components: {
    CommentsToggle,
    DocumentAtsDropdown,
    DocumentDropdown,
    DuplicateModal,
    ExportModalPostEditor,
    SimilarJobs, // TODO: remove after new similar jobs implementation is complete
    HistoryToggle,
    NotifyDropdown,
    ShareModalButton,
    SyncModalButton,
    FirstDraftButtonForSubmenu,
    SimilarJobsToggle,
    TranslateJobButton,
  },
  props: [
    'isTemplate',
    'jobId',
    'isLinked',
    'hasIntegration',
    'tapwageId',
    'projectIntegrationType',
    'projectIntegrationTypeKey',
    'isPublished',
    'isOpen',
    'atsEditUrl',
    'atsName',
    'atsIcon',
    'isSharePage',
    'jobStatusLabel',
    'superuser',
    'isSyncable',
    'isPermissionTogglable',
    'isPrivate',
    'hasShareUsers',
    'revisionLast',
    'allRevisions',
    'revisionId',
    'revisionList',
    'ownersActive',
    'ownersLead',
    'shareNames',
    'validUsers',
    'location',
    'company',
    'jobTitle',
    'companyName',
    'showInvite',
    'isDataLoaded',
    'isInternal',
    'isEvergreen',
    'isHidden',
    'isFlagship',
  ],
  emits: ['tracking-event'],
  setup() {
    const { visible: translateJobVisible } = useTranslateJobButton();
    return { translateJobVisible };
  },
  data() {
    return {
      duplicateModalVisible: false,
      notificationSuccessModalVisible: false,
      notificationErrorModalVisible: false,
      TYPE_TAPWAGE,
    };
  },
  computed: {
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useEditor, ['documentType', 'isEditable', 'isTapwageIntegration']),
    showSyncOrExport() {
      return !this.isTemplate && !this.isSharePage;
    },
    aiEnabled() {
      return this.authData.aiEnabled;
    },
    hideApplyTemplate() {
      return this.isTapwageIntegration && ['Open', 'Unpublished'].includes(this.jobStatusLabel);
    },
  },
  methods: {
    ...mapActions(useApplyTemplate, { setApplyTemplateModalVisible: 'setModalVisible' }),
    ...mapActions(useLibrary, ['duplicateDocumentAndRedirect']),

    onDuplicate({ isPrivate, title }) {
      this.$emit('tracking-event', {
        action: 'editorLinkClick', args: { label: 'Duplicate Submit' },
      });
      if (this.isFlagship) {
        this.$emit('tracking-event', {
          action: 'flagshipDuplicate',
          args: { origin: 'Duplicate in Topbar' },
        });
      }
      return this.duplicateDocumentAndRedirect({
        jobId: this.jobId,
        isPrivate,
        title,
        from: 'editor',
        router: this.$router,
      });
    },
  },
};
</script>
<style scoped lang="less">
.submenu {
  width: 100%;
  height: 57px;
  z-index: 8;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: auto;
}
.submenu-container {
  max-width: 1800px;
}
.submenu :deep(.clickable) {
  cursor: pointer;
}
.submenu :deep(a) {
  color: rgb(var(--v-theme-textDarkest));
  text-decoration: none;
}
.reporting-state {
  flex: 0 auto;
  padding: 10px 13px;
  display: flex;
  align-items: center;
  font-weight: 500;
  color: rgb(var(--v-theme-textDarkest));
  letter-spacing: 1px;
}
.reporting-state.template {
  color: @basic-blue;
}
.reporting-state.draft {
  color: @text-brand;
}
.reporting-state.open {
  color: @brand-primary;
}
.reporting-state.unpublished {
  color: @basic-orange;
}
.reporting-state.closed {
  color: @basic-red;
}
</style>
